import { ArrowRightOutlined, WarningOutlined } from '@ant-design/icons';
import { Flex, TableProps, Tooltip } from 'antd';
import Button from 'components/Button';
import { Key, SorterResult } from 'antd/es/table/interface';
import ProtectedResource from 'components/RoleBasedAccessControl/ProtectedResource';
import Table from 'components/Table';
import { useTranslation } from 'react-i18next';
import { routes } from 'routes/AllRoutes';
import { FarmerGroupV2 } from 'types/response';
import {
  getStakeholderTypeForCountry,
  isMobileScreenSize,
} from 'utils/helpers';
import useTrackerWrapper from 'hooks/useTracker';

type TablePropsFG = TableProps<FarmerGroupV2>;

type FarmerGroupTableProps = {
  country: string;
  data?: FarmerGroupV2[] | null;
  total: number;
  loading: boolean;
  currentPage: number;
  pageSize: number;
  sortedInfo: SorterResult<FarmerGroupV2>;
  selectedRow?: FarmerGroupV2;
  handlePageChange: (page: number, pageSize: number) => void;
  handleChange: TablePropsFG['onChange'];
  handleAction: (data: FarmerGroupV2, drawer?: string) => void;
};

const sortHandler = (
  sort: {
    columnKey?: SorterResult<unknown>['columnKey'];
    order?: SorterResult<unknown>['order'];
  },
  key: Key,
) => {
  return sort?.columnKey === key ? sort?.order : null;
};

const FarmerGroupTable = ({
  country,
  data,
  total,
  loading = false,
  currentPage,
  pageSize,
  selectedRow,
  sortedInfo,
  handlePageChange,
  handleChange,
  handleAction,
}: FarmerGroupTableProps) => {
  const { t } = useTranslation();
  const { onClickWrapper } = useTrackerWrapper();

  const handleClickDetail = (data: FarmerGroupV2) => {
    onClickWrapper('Farmer Group Detail', () => handleAction(data));
  };

  const farmerGroupLeaderTableHeader: TablePropsFG['columns'] = [
    {
      title: t('common.farmerGroup'),
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: sortHandler(sortedInfo, 'name'),
      onCell: (data) => ({ onClick: () => handleClickDetail(data) }),
      className: 'clickable',
      render: (val) => (
        <Flex align="center" justify="space-between" gap={4}>
          {val}
          {!isMobileScreenSize ? (
            <Button className="table-click-indicator" type="text">
              <ArrowRightOutlined />
            </Button>
          ) : null}
        </Flex>
      ),
      width: 220,
    },
    {
      title: t('common.farmerGroupLeader'),
      dataIndex: ['groupLeader', 'name'],
      key: 'rizeusersByCubeLeader_name',
      width: 220,
      render: (name, record) => (
        <Flex align="center" gap={4}>
          {name}
          {record.groupLeader?.id &&
          record.groupLeader?.status?.toLowerCase() !== 'active' ? (
            <Tooltip title={t('stakeholders.thisStakeholderMissingInfo')}>
              <WarningOutlined
                style={{ color: '#FF4D4F' }}
                onClick={() =>
                  window.open(
                    routes.farmerGroupLeader +
                      '?leaderId=' +
                      record.groupLeader?.id,
                    '_blank',
                  )
                }
              />
            </Tooltip>
          ) : null}
        </Flex>
      ),
    },
    {
      title: getStakeholderTypeForCountry(country, 'PB'),
      dataIndex: ['paddyBuyer', 'name'],
      key: 'paddyBuyer_name',
      width: 220,
      render: (name, record) => (
        <Flex align="center" gap={4}>
          {name}
          {record.paddyBuyer?.id &&
          record.paddyBuyer?.status?.toLowerCase() !== 'active' ? (
            <Tooltip title={t('stakeholders.thisStakeholderMissingInfo')}>
              <WarningOutlined
                style={{ color: '#FF4D4F' }}
                onClick={() =>
                  window.open(
                    routes.paddyBuyer +
                      '?paddyBuyerId=' +
                      record.paddyBuyer?.id,
                    '_blank',
                  )
                }
              />
            </Tooltip>
          ) : null}
        </Flex>
      ),
    },
    {
      title: t('common.juniorAgronomistEmail'),
      dataIndex: ['rizeusersByFieldCoordinator', 'email'],
      key: 'rizeusersByFieldCoordinator_email',
      render: (jrAgronomist) =>
        jrAgronomist ?? <span className="none-label">No Assigned Yet</span>,
      width: 220,
    },
    {
      title: t('common.filters.awdAgronomist'),
      dataIndex: ['almCoordinator', 'email'],
      key: 'almCoordinator_email',
      render: (awdAg, data) =>
        awdAg ?? (
          <ProtectedResource permission="farmer_group:assign">
            <a className="link" onClick={() => handleAction(data, 'assign')}>
              <Flex gap={4} align="center">
                {t('farmerGroups.assignAgronomist')}
              </Flex>
            </a>
          </ProtectedResource>
        ),
    },
    {
      title: t('common.createdBy'),
      dataIndex: 'createdByEmail',
      key: 'createdByEmail',
      width: 220,
    },
    {
      title: t('common.ownedHectares'),
      dataIndex: 'cubeOwnedHectares',
      key: 'cubeOwnedHectares',
      width: 160,
    },
    {
      title: t('common.totalFarmers'),
      dataIndex: 'totalFarmers',
      key: 'totalFarmers',
      width: 160,
    },
    {
      title: t('common.subDistrict'),
      dataIndex: ['address', 'subDistrict'],
      key: 'subDistrict',
      sorter: true,
      sortOrder: sortHandler(sortedInfo, 'subDistrict'),
      width: 180,
    },
  ];

  const isHighlighted = (record: FarmerGroupV2) => {
    const isStakeholderInactive =
      record?.groupLeader?.status?.toLowerCase() !== 'active' ||
      record?.paddyBuyer?.status?.toLowerCase() !== 'active';

    if (selectedRow) return selectedRow?.id === record?.id;

    return isStakeholderInactive;
  };

  return (
    <Table<FarmerGroupV2>
      tableColumns={data ?? []}
      tableHeader={farmerGroupLeaderTableHeader}
      loading={loading}
      onChange={handleChange}
      setHighlighted={isHighlighted}
      highlightColor={selectedRow ? 'gray' : 'red'}
      pagination={{
        total: total,
        current: currentPage,
        pageSize: pageSize,
        onChange: handlePageChange,
        onShowSizeChange: handlePageChange,
      }}
    />
  );
};

export default FarmerGroupTable;
